import type {
  B2bCompletionQuestionDto,
  LookupGroup,
  ProfileCompletionQuestionDto,
  SaveResponseFileItem,
  ScreenerAnswerForParticipantDto,
  ScreenerQuestionForParticipantDto,
  ScreenerResponseStatus,
} from '@/api/core-api/types.gen';

// Re-export for convenience
export type {
  ScreenerQuestionForParticipantDto,
  ScreenerAnswerForParticipantDto,
};

export type ProfileCompletionOption = NonNullable<
  ProfileCompletionQuestionDto['options']
>[number];

/**
 * Attribute a prequalification-style question fills: a registry profile
 * attribute, or — in the public apply flow only — one of the bespoke B2B
 * employment fields. The B2B keys that name a registry attribute (industry,
 * companySize, seniorityLevel, jobFunctions) share its value and write the same
 * `employment.*` field; only the bespoke trio (email, jobTitle, company) is
 * B2B-only.
 */
export type ProfileQuestionAttributeKey =
  | ProfileCompletionQuestionDto['attributeKey']
  | B2bCompletionQuestionDto['attributeKey'];

/**
 * Question type constants for easy reference.
 *
 * These values must be kept in sync with the generated `QuestionType` enum
 * from `@/api/core-api/types.gen`. When the API schema changes, regenerate
 * the types (`pnpm nx run core-api:generateModels`) and update this map
 * accordingly.
 *
 * The generated enum looks like:
 *   export const QuestionType = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, ... }
 */
export const QUESTION_TYPES = {
  RADIO: 1,
  MULTI_LINE_TEXTBOX: 2,
  CHECKBOX: 3,
  SINGLE_LINE_TEXTBOX: 4,
  NUMERIC_BOX: 5,
  SLIDER_SCALE: 6,
  FILE_UPLOAD: 7,
  RANK: 8,
  MATRIX: 9,
  DATE: 10,
  LOOKUP_MULTI_SELECT: 11,
} as const;

export interface ScreenerAnswerValue {
  id?: string;
  uid: string;
  text: string;
  value?: string;
  isOther?: boolean;
  otherText?: string;
}

export interface PrequalificationAnswerValue {
  value: string;
  text: string;
}

export interface SkippedResponse {
  questionId: string;
  questionUid?: string;
  questionType: (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES];
  skipped: true;
  skippedBy?: 'user';
}

export interface ScreenerRadioResponse {
  questionId: string;
  questionUid?: string;
  questionType: typeof QUESTION_TYPES.RADIO;
  answer: ScreenerAnswerValue;
}

export interface ScreenerCheckboxResponse {
  questionId: string;
  questionUid?: string;
  questionType: typeof QUESTION_TYPES.CHECKBOX;
  answers: ScreenerAnswerValue[];
}

export interface ScreenerTextResponse {
  questionId: string;
  questionUid?: string;
  questionType:
    | typeof QUESTION_TYPES.SINGLE_LINE_TEXTBOX
    | typeof QUESTION_TYPES.MULTI_LINE_TEXTBOX;
  text: string;
}

export interface ScreenerNumericResponse {
  questionId: string;
  questionUid?: string;
  questionType:
    | typeof QUESTION_TYPES.NUMERIC_BOX
    | typeof QUESTION_TYPES.SLIDER_SCALE;
  value: number;
}

export type ScreenerFileItem = SaveResponseFileItem;

export interface ScreenerFileResponse {
  questionId: string;
  questionUid?: string;
  questionType: typeof QUESTION_TYPES.FILE_UPLOAD;
  files: ScreenerFileItem[];
  note?: string;
}

export interface ScreenerRankItem {
  uid: string;
  text: string;
}

export interface ScreenerRankResponse {
  questionId: string;
  questionUid?: string;
  questionType: typeof QUESTION_TYPES.RANK;
  ranking: ScreenerRankItem[];
}

export interface ScreenerMatrixSelection {
  rowUid: string;
  rowText: string;
  answerUid: string;
  answerText: string;
}

export interface ScreenerMatrixResponse {
  questionId: string;
  questionUid?: string;
  questionType: typeof QUESTION_TYPES.MATRIX;
  selections: ScreenerMatrixSelection[];
}

export type ScreenerResponse =
  | SkippedResponse
  | ScreenerRadioResponse
  | ScreenerCheckboxResponse
  | ScreenerTextResponse
  | ScreenerNumericResponse
  | ScreenerFileResponse
  | ScreenerRankResponse
  | ScreenerMatrixResponse;

export interface PrequalificationRadioResponse {
  attributeKey: ProfileQuestionAttributeKey;
  questionType: typeof QUESTION_TYPES.RADIO;
  answer: PrequalificationAnswerValue;
}

export interface PrequalificationCheckboxResponse {
  attributeKey: ProfileQuestionAttributeKey;
  questionType: typeof QUESTION_TYPES.CHECKBOX;
  answers: PrequalificationAnswerValue[];
}

export interface PrequalificationTextResponse {
  attributeKey: ProfileQuestionAttributeKey;
  questionType:
    | typeof QUESTION_TYPES.SINGLE_LINE_TEXTBOX
    | typeof QUESTION_TYPES.MULTI_LINE_TEXTBOX;
  text: string;
}

export interface PrequalificationNumericResponse {
  attributeKey: ProfileQuestionAttributeKey;
  questionType:
    | typeof QUESTION_TYPES.NUMERIC_BOX
    | typeof QUESTION_TYPES.SLIDER_SCALE;
  value: number;
}

export interface PrequalificationDateResponse {
  attributeKey: ProfileQuestionAttributeKey;
  questionType: typeof QUESTION_TYPES.DATE;
  /**
   * Calendar date as `YYYY-MM-DD`, matching what onboarding writes to
   * `demographics.dateOfBirth`. A `Date` would survive neither the JSON round
   * trip through the public-apply draft buffer nor the profile update, which
   * takes a plain date with no time or zone attached.
   */
  value: string;
}

export type PrequalificationResponse =
  | PrequalificationRadioResponse
  | PrequalificationCheckboxResponse
  | PrequalificationTextResponse
  | PrequalificationDateResponse
  | PrequalificationNumericResponse;

export type FormResponse = ScreenerResponse | PrequalificationResponse;

export interface ScreenerFormState {
  currentStep: number;
  responses: Record<string, ScreenerResponse>;
  startTime: number;
  lastSaveTime: number;
}

/**
 * Extended answer option that adds an optional `value` field to the generated DTO.
 * Screener answers don't currently set `value`, but prequalification wrappers
 * reuse the same choice rendering patterns and may provide it.
 */
export type ScreenerAnswerOption = ScreenerAnswerForParticipantDto & {
  value?: string;
};

export type RenderableScreenerQuestion = Omit<
  ScreenerQuestionForParticipantDto,
  'answers'
> & {
  answers?: ScreenerAnswerOption[];
};

export type ScreenerFormQuestion = RenderableScreenerQuestion & {
  questionSource: 'screener';
};

export interface PrequalificationFormQuestion {
  questionSource: 'prequalification';
  attributeKey: ProfileQuestionAttributeKey;
  text: string;
  questionType: ProfileCompletionQuestionDto['questionType'];
  isRequired: true;
  options: ProfileCompletionOption[];
  /**
   * LOOKUP_MULTI_SELECT only: the lookup the question searches for choices
   * (e.g. industries). Answers keep the checkbox shape; values are stored
   * verbatim.
   */
  lookupGroup?: LookupGroup;
}

export type FormQuestion = ScreenerFormQuestion | PrequalificationFormQuestion;

interface BaseQuestionComponentProps<TQuestion, TResponse> {
  question: TQuestion;
  value?: TResponse;
  onChange: (value?: TResponse) => void;
  disabled?: boolean;
  /**
   * Participant-stable string used to seed display-order randomization. Absent
   * outside the real participant flow (e.g. researcher preview), where a fresh
   * random order per mount is the desired behaviour.
   */
  shuffleSeed?: string;
  /** Called whenever the component transitions between "uploads in-flight" and "idle". */
  onUploadingChange?: (isUploading: boolean) => void;
  /**
   * Called when the participant attempts to paste into a free-text answer.
   * Free-text components block the paste itself (answers must be typed so
   * researchers get original responses); this callback lets the parent show
   * a warning and record the attempt.
   */
  onPasteAttempt?: () => void;
}

export type ScreenerQuestionComponentProps<
  T extends ScreenerResponse = ScreenerResponse,
> = BaseQuestionComponentProps<RenderableScreenerQuestion, T>;

export type PrequalificationQuestionComponentProps<
  T extends PrequalificationResponse = PrequalificationResponse,
> = BaseQuestionComponentProps<PrequalificationFormQuestion, T>;

// NDA info type
export interface NdaInfo {
  requiresNda?: boolean;
  ndaType?: 'none' | 'file' | 'link' | null;
  ndaUrl?: string | null;
}

// Existing response type for draft resumption
export interface ExistingResponse {
  id?: string;
  status?: ScreenerResponseStatus;
  // Question IDs that have been answered (for proper skip logic handling on resume)
  answeredQuestionIds?: string[];
  // Whether all reachable questions have been answered (computed by backend using skip logic)
  isScreenerComplete?: boolean;
}
